// @flow
/* eslint jsx-a11y/no-static-element-interactions:0 */

import React, { Component } from 'react';
import BoardSuggestionListRow from 'app/common/react/components/save/pin-to-board/BoardSuggestionList/BoardSuggestionListRow';
import i18n from 'app/common/lib/i18n';
import { stopTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { track } from 'app/common/lib/react/analytics';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Text, Spinner, Touchable } from 'gestalt';

const UNSELECTED_INDEX = -1;
/**
 * BoardSuggestionList
 * Manages the list of suggested board titles for repin.
 */
type BoardNameSuggestionType = Array<{ metadata: string, title: string }>;
type OwnProps = {|
  onBoardSelected: (boardName: string, oneTap: boolean) => void,
  pinId: ?string,
  inFirstBoardCreateGestaltExp: ?boolean,
  image: ?{
    height: number,
    url: string,
    width: number,
  },
  inFlyoutExp: ?boolean,
  minHeight: ?number,
  error?: any,
|};

type HOCProps = {|
  boards: Resource<BoardNameSuggestionType>,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
|};

type State = {|
  selectedIndex: number,
  suggestedBoardsNames: Array<string>,
|};

export class BoardSuggestionList extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedIndex: UNSELECTED_INDEX,
      suggestedBoardsNames: this._processSuggestions(props.boards.data),
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.setState({ suggestedBoardsNames: this._processSuggestions(nextProps.boards.data) });
  }

  componentDidUpdate(prevProps: Props) {
    const { inFirstBoardCreateGestaltExp, boards } = this.props;
    if (!prevProps.boards.isLoaded && boards.isLoaded && inFirstBoardCreateGestaltExp) {
      this.setPWTLoadingEnd();
    }
  }

  setPWTLoadingEnd() {
    stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PICKER, {
      error: !!this.props.error,
    });
  }

  _processSuggestions = (boardNameSuggestions: ?BoardNameSuggestionType): Array<string> =>
    boardNameSuggestions && boardNameSuggestions.length
      ? boardNameSuggestions.map((item) => item.title)
      : [];

  handleClick: (index: number) => void = (index) => {
    const { suggestedBoardsNames } = this.state;
    const { onBoardSelected } = this.props;
    onBoardSelected(suggestedBoardsNames[index], false);
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.PIN_CREATE_BOARD_PICKER,
      element: PCONST.thrift.ElementType.BOARD_CREATE_SUGGESTED,
    });
  };

  handleMouseEnter: (index: number) => void = (index) => {
    this.setState({ selectedIndex: index });
  };

  handleMouseLeave: () => void = () => {
    this.setState({ selectedIndex: UNSELECTED_INDEX });
  };

  renderSuggestionRow = (boardName: string, index: number) => {
    const selected = this.state.selectedIndex === index;
    return (
      <li key={index}>
        <Touchable
          onTouch={() => this.handleClick(index)}
          onMouseEnter={() => this.handleMouseEnter(index)}
          onMouseLeave={this.handleMouseLeave}
        >
          <BoardSuggestionListRow
            selected={selected}
            name={boardName}
            inFlyoutExp={this.props.inFlyoutExp}
            image={this.props.image}
          />
        </Touchable>
      </li>
    );
  };

  renderGestaltBoardSuggestionList() {
    const { suggestedBoardsNames } = this.state;
    // TODO(evaldez): Swap in when i18n complete
    //eslint-disable-next-line no-unused-vars
    const TEMP_NEW_SUGGESTED_BOARD_NAMES_STRING = i18n._(
      'Suggested board names',
      'text above suggested board names list',
    );
    return (
      <Box>
        <Box marginTop={5} marginLeft={6} marginBottom={3}>
          <Text size="md">{i18n._('Suggested board names')}</Text>
        </Box>
        <ul>{suggestedBoardsNames.map(this.renderSuggestionRow)}</ul>
      </Box>
    );
  }

  renderPreviewBoardSuggestionList() {
    const { suggestedBoardsNames } = this.state;
    return (
      <div>
        <ul>{suggestedBoardsNames.map(this.renderSuggestionRow)}</ul>
      </div>
    );
  }

  render() {
    const { inFirstBoardCreateGestaltExp, inFlyoutExp, minHeight, boards } = this.props;
    const { suggestedBoardsNames } = this.state;

    if (!boards.isLoaded) {
      return (
        <Box
          alignItems="center"
          display="flex"
          height="100%"
          justifyContent="center"
          minHeight={minHeight || ''}
        >
          <Spinner
            accessibilityLabel={i18n._(
              'Loading board suggestions',
              'Accesibility label for loading board suggestions',
            )}
            show
          />
        </Box>
      );
    }

    if (!suggestedBoardsNames || !suggestedBoardsNames.length) {
      return null;
    }

    if (inFlyoutExp) {
      return this.renderPreviewBoardSuggestionList();
    }

    return inFirstBoardCreateGestaltExp ? (
      this.renderGestaltBoardSuggestionList()
    ) : (
      <Box>
        <Box marginTop={2} paddingX={5}>
          <Text>{i18n._('Suggested board names')}</Text>
        </Box>
        <ul>{suggestedBoardsNames.map(this.renderSuggestionRow)}</ul>
      </Box>
    );
  }
}

export default withResource({
  name: 'BoardTitleSuggestionsResource',
  options: ({ pinId }: { pinId: string }) => ({
    pin_id: pinId,
  }),
  key: 'boards',
})(BoardSuggestionList);
